import {
    Button,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Text,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
    resetAuthCode,
    verifyAuthCode,
} from "../../../api";
import { colors } from "../../../styles";
import { ISignUpUser } from "../../../types";
import { getSignUpErrorMessage } from "./errors";
import StepLayout, {
    FieldMessage,
    underlineInputProps,
} from "./StepLayout";

interface IPhoneAuthProps {
    progress: number;
    userData: Partial<ISignUpUser>;
    onNext: () => void;
}

interface IAuthForm {
    authCode: string;
}

const AUTH_CODE_EXPIRES_IN = 180;
const RESEND_COOLDOWN = 60;

const authCodeRegex = /^\d{6}$/;

const isAuthCodeValid = (
    value?: string
) => {
    return authCodeRegex.test(
        value ?? ""
    );
};

export default function PhoneAuth({
    progress,
    userData,
    onNext,
}: IPhoneAuthProps) {

    /* 토스트 */

    const toast = useToast();

    /* 상태 */

    const [errorMessage, setErrorMessage] =
        useState("");

    const [timeLeft, setTimeLeft] =
        useState(AUTH_CODE_EXPIRES_IN);

    const [
        resendCooldown,
        setResendCooldown,
    ] = useState(RESEND_COOLDOWN);

    /* phone */

    const { phone } = userData;

    /* useForm */

    const {
        register,
        handleSubmit,
        watch,
    } = useForm<IAuthForm>();

    const authCode =
        watch("authCode") ?? "";

    /* 타이머 */

    useEffect(() => {

        const interval = setInterval(() => {

            setTimeLeft((prev) =>
                Math.max(prev - 1, 0)
            );

            setResendCooldown((prev) =>
                Math.max(prev - 1, 0)
            );

        }, 1000);

        return () => {
            clearInterval(interval);
        };

    }, []);

    /* 인증번호 만료 */

    useEffect(() => {

        if (timeLeft !== 0) {
            return;
        }

        setErrorMessage(
            "인증번호가 만료되었습니다. 다시 전송해주세요."
        );

    }, [timeLeft]);

    /* 타이머 포맷 */

    const formatTimeLeft = (
        seconds: number
    ) => {

        const minutes =
            Math.floor(seconds / 60);

        const remainingSeconds =
            seconds % 60;

        return `${String(minutes).padStart(
            2,
            "0"
        )}:${String(
            remainingSeconds
        ).padStart(2, "0")}`;
    };

    /* 재전송 */

    const resetAuthCodeMutation =
        useMutation({
            mutationFn: resetAuthCode,
            retry: false,
        });

    /* 인증 확인 */

    const verifyAuthCodeMutation =
        useMutation({
            mutationFn: verifyAuthCode,
            retry: false,
        });

    /* 인증번호 재전송 */

    const onReset = async () => {

        if (!phone) {
            return;
        }

        if (resendCooldown > 0) {
            return;
        }

        setErrorMessage("");

        try {

            const result =
                await resetAuthCodeMutation.mutateAsync({
                    phone,
                });

            ////////////////////////////////////////////////////////////
            // 새 인증번호 기준으로 만료시간 초기화
            //
            // 서버가 expiresIn을 반환한다면 그것을 우선 사용

            setTimeLeft(
                result?.expiresIn ??
                AUTH_CODE_EXPIRES_IN
            );

            ////////////////////////////////////////////////////////////
            // 재전송 cooldown 초기화

            setResendCooldown(
                RESEND_COOLDOWN
            );

            ////////////////////////////////////////////////////////////
            // 성공 알림

            toast({
                title:
                    "인증번호가 재전송되었습니다.",
                description:
                    "인증번호를 확인해주세요.",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top",
            });

        } catch (error) {

            setErrorMessage(
                getSignUpErrorMessage(
                    error,
                    "인증번호 재전송 중 문제가 발생했습니다."
                )
            );
        }
    };

    /* 인증번호 확인 */

    const onSubmit = async ({
        authCode: nextAuthCode,
    }: IAuthForm) => {

        if (!phone) {
            return;
        }

        setErrorMessage("");

        try {

            ////////////////////////////////////////////////////////////
            // 인증번호 검증
            //
            // 성공하면 서버가 phone:auth:verified 상태를 생성

            await verifyAuthCodeMutation.mutateAsync({
                phone,
                authCode: nextAuthCode,
            });

            ////////////////////////////////////////////////////////////
            // 다음 회원가입 단계

            onNext();

        } catch (error) {

            setErrorMessage(
                getSignUpErrorMessage(
                    error,
                    "인증번호 확인 중 문제가 발생했습니다."
                )
            );
        }
    };

    /* render */

    return (
        <StepLayout
            progress={progress}
            titles={[
                "인증번호를 입력해주세요.",
            ]}
            asForm
            onSubmit={handleSubmit(onSubmit)}
        >

            <FormControl>

                <FormLabel
                    fontSize="sm"
                    color={colors.blue}
                >
                    인증번호
                </FormLabel>

                <HStack>

                    <Input
                        {...register(
                            "authCode",
                            {
                                required: true,
                                pattern:
                                    authCodeRegex,
                            }
                        )}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="인증번호 입력"
                        {...underlineInputProps}
                    />

                    <VStack
                        justifyContent="center"
                        alignItems="center"
                        marginLeft={2}
                    >
                        <Text
                            color={colors.red}
                            fontSize="sm"
                        >
                            {formatTimeLeft(
                                timeLeft
                            )}
                        </Text>
                    </VStack>

                    <Button
                        type="button"
                        variant="outline"
                        color={colors.g1}
                        size="sm"
                        onClick={onReset}
                        marginLeft={2}
                        isLoading={
                            resetAuthCodeMutation.isPending
                        }
                        isDisabled={
                            resendCooldown > 0
                        }
                    >
                        {resendCooldown > 0
                            ? `재전송 (${resendCooldown})`
                            : "재전송"}
                    </Button>

                </HStack>

            </FormControl>

            <FieldMessage
                message={errorMessage}
            />

            <Button
                type="submit"
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={5}
                isLoading={
                    verifyAuthCodeMutation.isPending
                }
                isDisabled={
                    !isAuthCodeValid(authCode) ||
                    timeLeft === 0
                }
            >
                <Text
                    fontSize={16}
                    fontWeight={700}
                >
                    다음
                </Text>
            </Button>

        </StepLayout>
    );
}